// src/library/typeEffect.tsx
import { useEffect, useMemo, useRef, useState } from "react";

interface TypingEffectProps {
  text: string;
  /** Epoch ms when the message started “typing” server-side */
  startTime: number;
}

/**
 * Smooth typing animation that:
 * - seeds initial progress from (now - startTime)
 * - uses rAF for smoothness instead of setInterval
 * - respects prefers-reduced-motion (renders full text immediately)
 * - adapts to text changes without popping
 */
export function TypingEffect({ text, startTime }: TypingEffectProps) {
  const [length, setLength] = useState(0);

  // Keep latest text and length in refs for rAF loop without stale closures
  const latestTextRef = useRef(text);
  latestTextRef.current = text;

  const lengthRef = useRef(0);

  // Local helper to update both state and ref in one place
  const setLen = (updater: (prev: number) => number) => {
    setLength((prev) => {
      const next = updater(prev);
      lengthRef.current = next;
      return next;
    });
  };

  // Typing speed calibrated to previous behavior (~1 char per 5ms)
  const CHARS_PER_MS = 0.2;

  // Respect user preference for reduced motion
  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    // Reduced motion: render full text immediately
    if (reduceMotion) {
      lengthRef.current = text.length;
      setLength(text.length);
      return;
    }

    const now = Date.now();
    const elapsed = Math.max(0, now - (startTime || 0));
    const seeded = Math.min(text.length, Math.max(0, Math.floor(elapsed * CHARS_PER_MS)));

    // Seed initial progress; never go backwards unless text shrank
    setLen((prev) => Math.max(Math.min(prev, text.length), seeded));

    // Already complete? No animation needed
    if (seeded >= text.length) return;

    // If startTime is in the future, delay until then
    const startDelay = Math.max(0, startTime - now);

    let frameId: number | null = null;
    let lastTs = 0;

    const start = () => {
      lastTs = performance.now();

      const tick = (ts: number) => {
        const targetLen = latestTextRef.current.length;
        const dt = ts - lastTs;
        lastTs = ts;

        // Increment progress towards current target length
        setLen((prev) => {
          // If text shrank, clamp immediately
          const current = Math.min(prev, targetLen);
          const inc = dt * CHARS_PER_MS;
          const next = Math.min(targetLen, Math.floor(current + inc));
          return next;
        });

        // Continue until fully revealed
        if (lengthRef.current < targetLen) {
          frameId = requestAnimationFrame(tick);
        } else {
          frameId = null;
        }
      };

      frameId = requestAnimationFrame(tick);
    };

    const delayTimer =
      startDelay > 0 ? window.setTimeout(start, startDelay) : (start(), 0);

    return () => {
      if (delayTimer) window.clearTimeout(delayTimer);
      if (frameId != null) cancelAnimationFrame(frameId);
    };
  }, [text, startTime, reduceMotion]);

  const safeLen = Math.min(length, text.length);
  return <span>{text.substring(0, safeLen)}</span>;
}
